import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';

const ROTATION_MIN = -360;
const ROTATION_MAX = 360;
const PREVIEW_ZOOM_MIN = 0.25;
const PREVIEW_ZOOM_MAX = 3;
const PREVIEW_ZOOM_STEP = 0.1;

const clampRotation = (value) => Math.max(ROTATION_MIN, Math.min(ROTATION_MAX, value));
const clampPreviewZoom = (value) => Math.max(PREVIEW_ZOOM_MIN, Math.min(PREVIEW_ZOOM_MAX, value));

/**
 * Image Editor Component
 * Provides image editing capabilities: rotate, scale, remove alpha, superimpose, transparency
 * Non-destructive editing pipeline until save
 * Saves back to original file or controlled overwrite
 */

const ImageEditor = ({ file, onSave, onClose }) => {
  const canvasRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const isInitialRenderRef = useRef(true);

  const [originalImage, setOriginalImage] = useState(null);
  const [overlayImage, setOverlayImage] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [scale, setScale] = useState(1);
  const [previewZoom, setPreviewZoom] = useState(1);
  const [transparency, setTransparency] = useState(1);
  const [removeAlpha, setRemoveAlpha] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [overlayPosition, setOverlayPosition] = useState({ x: 0, y: 0 });
  const [isDraggingOverlay, setIsDraggingOverlay] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isMaximized, setIsMaximized] = useState(false);

  useEffect(() => {
    if (file && file.content) {
      loadImage(file.content);
    }
  }, [file]);

  const loadImage = (imageSrc) => {
    const img = new Image();
    img.onload = () => {
      isInitialRenderRef.current = true;
      setOriginalImage(img);
      setImageSize({ width: img.width, height: img.height });
      setHasChanges(false);
    };
    img.onerror = () => {
      toast.error('Failed to load image');
    };
    img.src = imageSrc;
  };

  const applyTransformations = useCallback((markAsChanged = true) => {
    if (!originalImage || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Keep a stable canvas bounding box while rotating to avoid visual "zoom"/bounce
    // Use the scaled image diagonal so any rotation fits inside without resizing canvas each frame.
    const angle = (rotation * Math.PI) / 180;
    const scaledWidth = originalImage.width * scale;
    const scaledHeight = originalImage.height * scale;
    const stableCanvasSize = Math.ceil(Math.sqrt((scaledWidth ** 2) + (scaledHeight ** 2)) + 4);
    
    canvas.width = stableCanvasSize;
    canvas.height = stableCanvasSize;
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Apply transformations
    ctx.save();
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(angle);
    ctx.scale(scale, scale);
    
    // Handle alpha removal
    if (removeAlpha) {
      ctx.fillStyle = 'white';
      ctx.fillRect(-originalImage.width / 2, -originalImage.height / 2, originalImage.width, originalImage.height);
    }
    
    ctx.globalAlpha = transparency;
    ctx.drawImage(originalImage, -originalImage.width / 2, -originalImage.height / 2);
    ctx.restore();
    
    // Draw overlay if present
    if (overlayImage && overlayCanvasRef.current) {
      drawOverlay();
    }

    setHasChanges(markAsChanged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalImage, rotation, scale, transparency, removeAlpha, overlayImage, overlayPosition]);

  const drawOverlay = useCallback(() => {
    if (!overlayImage || !overlayCanvasRef.current || !canvasRef.current) return;

    const overlayCanvas = overlayCanvasRef.current;
    const overlayCtx = overlayCanvas.getContext('2d');
    const mainCanvas = canvasRef.current;
    
    overlayCanvas.width = mainCanvas.width;
    overlayCanvas.height = mainCanvas.height;
    
    overlayCtx.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);
    overlayCtx.globalAlpha = transparency * 0.7; // Overlay is slightly transparent
    overlayCtx.drawImage(
      overlayImage,
      overlayPosition.x,
      overlayPosition.y,
      overlayImage.width * scale,
      overlayImage.height * scale
    );
  }, [overlayImage, overlayPosition, transparency, scale]);

  useEffect(() => {
    if (originalImage) {
      applyTransformations(!isInitialRenderRef.current);
      if (isInitialRenderRef.current) {
        isInitialRenderRef.current = false;
      }
    }
  }, [originalImage, rotation, scale, transparency, removeAlpha, overlayImage, overlayPosition, applyTransformations]);

  const handleRotate = (step) => {
    setRotation((prev) => clampRotation(prev + step));
  };

  const handleRotationChange = (newRotation) => {
    setRotation(clampRotation(newRotation));
  };

  const handleScale = (newScale) => {
    setScale(Math.max(0.1, Math.min(3, newScale)));
  };

  const handlePreviewZoom = (newZoom) => {
    setPreviewZoom(clampPreviewZoom(newZoom));
  };

  const handlePreviewZoomStep = (step) => {
    setPreviewZoom((prev) => clampPreviewZoom(parseFloat((prev + step).toFixed(2))));
  };

  const handleTransparency = (newTransparency) => {
    setTransparency(Math.max(0, Math.min(1, newTransparency)));
  };

  const handleReset = () => {
    setRotation(0);
    setScale(1);
    setPreviewZoom(1);
    setTransparency(1);
    setRemoveAlpha(false);
    setOverlayImage(null);
    setOverlayPosition({ x: 0, y: 0 });
  };

  const handleLoadOverlay = () => {
    fileInputRef.current?.click();
  };

  const handleOverlayFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        setOverlayImage(img);
        toast.success('Overlay image loaded');
      };
      img.onerror = () => {
        toast.error('Failed to load overlay image');
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleOverlayMouseDown = (e) => {
    if (!overlayImage) return;
    
    setIsDraggingOverlay(true);
    const rect = overlayCanvasRef.current.getBoundingClientRect();
    const zoomFactor = previewZoom * scale;
    setDragStart({
      x: ((e.clientX - rect.left) / zoomFactor) - overlayPosition.x,
      y: ((e.clientY - rect.top) / zoomFactor) - overlayPosition.y
    });
  };

  const handleOverlayMouseMove = (e) => {
    if (!isDraggingOverlay || !overlayCanvasRef.current) return;
    
    const rect = overlayCanvasRef.current.getBoundingClientRect();
    const zoomFactor = previewZoom * scale;
    setOverlayPosition({
      x: ((e.clientX - rect.left) / zoomFactor) - dragStart.x,
      y: ((e.clientY - rect.top) / zoomFactor) - dragStart.y
    });
  };

  const handleOverlayMouseUp = () => {
    setIsDraggingOverlay(false);
  };

  const handleSave = async () => {
    if (!hasChanges || isProcessing) return;

    setIsProcessing(true);
    
    try {
      // Create final canvas with all transformations
      const finalCanvas = document.createElement('canvas');
      const finalCtx = finalCanvas.getContext('2d');
      
      const mainCanvas = canvasRef.current;
      finalCanvas.width = mainCanvas.width;
      finalCanvas.height = mainCanvas.height;
      
      // Draw main image
      finalCtx.drawImage(mainCanvas, 0, 0);
      
      // Draw overlay if present
      if (overlayImage && overlayCanvasRef.current) {
        finalCtx.drawImage(overlayCanvasRef.current, 0, 0);
      }
      
      // Convert to blob
      finalCanvas.toBlob(async (blob) => {
        try {
          await onSave(file, blob);
          setHasChanges(false);
          toast.success(`Saved ${file.name}`);
        } catch (error) {
          toast.error(`Failed to save ${file.name}: ${error.message}`);
        } finally {
          setIsProcessing(false);
        }
      }, 'image/png');
    } catch (error) {
      toast.error(`Failed to process image: ${error.message}`);
      setIsProcessing(false);
    }
  };

  const handleSaveAs = async () => {
    if (!canvasRef.current) return;

    try {
      const newFileName = window.prompt('Enter new file name:', file.name.replace(/\.[^/.]+$/, '') + '_edited.png');
      if (!newFileName) return;

      const fileHandle = await window.showSaveFilePicker({
        suggestedName: newFileName,
        types: [
          {
            description: 'PNG files',
            accept: { 'image/png': ['.png'] }
          }
        ]
      });

      const canvas = canvasRef.current;
      canvas.toBlob(async (blob) => {
        const writable = await fileHandle.createWritable();
        await writable.write(blob);
        await writable.close();
        toast.success(`Saved as ${newFileName}`);
      }, 'image/png');
    } catch (error) {
      if (error.name !== 'AbortError') {
        toast.error(`Failed to save as: ${error.message}`);
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      if (hasChanges) {
        if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
          onClose();
        }
      } else {
        onClose();
      }
    } else if ((e.ctrlKey || e.metaKey) && e.key === 's') {
      e.preventDefault();
      handleSave();
    }
  };

  const canvasDisplayStyle = {
    maxHeight: isMaximized ? 'calc(100vh - 250px)' : '70vh'
  };
  const effectivePreviewZoom = previewZoom * scale;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className={`theme-modal-backdrop fixed inset-0 flex items-center justify-center z-50 p-4 ${
        isMaximized ? 'p-0' : ''
      }`}
      onClick={onClose}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <motion.div
        initial={{ y: 20 }}
        animate={{ y: 0 }}
        className={`theme-modal rounded-lg shadow-2xl flex flex-col ${
          isMaximized
            ? 'w-full h-full rounded-none'
            : 'w-full max-w-7xl max-h-[95vh]'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{file.name}</h2>
              <p className="text-sm text-gray-500">
                Image Editor - {imageSize.width} × {imageSize.height}px
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {hasChanges && (
              <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                Unsaved changes
              </span>
            )}
            <button
              onClick={() => setIsMaximized(!isMaximized)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title={isMaximized ? "Restore" : "Maximize"}
            >
              {isMaximized ? (
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
              )}
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-2">
            {/* Rotation Controls */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleRotate(-90)}
                className="p-2 hover:bg-gray-200 rounded transition-colors"
                title="Rotate Left 90°"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12a9 9 0 109 9" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l4-4M3 12l4 4" />
                </svg>
              </button>
              <button
                onClick={() => handleRotate(-1)}
                className="px-2 py-1 text-xs bg-gray-200 hover:bg-gray-300 rounded transition-colors"
                title="Rotate Left 1°"
              >
                -1°
              </button>
              <input
                type="range"
                min={ROTATION_MIN}
                max={ROTATION_MAX}
                step="1"
                value={rotation}
                onChange={(e) => handleRotationChange(parseInt(e.target.value, 10))}
                className="w-32"
                title="Fine rotation control"
              />
              <button
                onClick={() => handleRotate(1)}
                className="px-2 py-1 text-xs bg-gray-200 hover:bg-gray-300 rounded transition-colors"
                title="Rotate Right 1°"
              >
                +1°
              </button>
              <button
                onClick={() => handleRotate(90)}
                className="p-2 hover:bg-gray-200 rounded transition-colors"
                title="Rotate Right 90°"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-9 9" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12l-4-4M21 12l-4 4" />
                </svg>
              </button>
              <span className="text-sm text-gray-600">Rotate: {rotation}° ({ROTATION_MIN}° to {ROTATION_MAX}°)</span>
            </div>

            <div className="w-px h-6 bg-gray-300" />

            {/* Preview Zoom Control */}
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Zoom:</label>
              <button
                onClick={() => handlePreviewZoomStep(-PREVIEW_ZOOM_STEP)}
                className="px-2 py-1 text-xs bg-gray-200 hover:bg-gray-300 rounded transition-colors"
                title="Zoom Out"
              >
                −
              </button>
              <input
                type="range"
                min={PREVIEW_ZOOM_MIN}
                max={PREVIEW_ZOOM_MAX}
                step={PREVIEW_ZOOM_STEP}
                value={previewZoom}
                onChange={(e) => handlePreviewZoom(parseFloat(e.target.value))}
                className="w-24"
                title="Preview zoom"
              />
              <button
                onClick={() => handlePreviewZoomStep(PREVIEW_ZOOM_STEP)}
                className="px-2 py-1 text-xs bg-gray-200 hover:bg-gray-300 rounded transition-colors"
                title="Zoom In"
              >
                +
              </button>
              <button
                onClick={() => setPreviewZoom(1)}
                className="px-2 py-1 text-xs bg-gray-200 hover:bg-gray-300 rounded transition-colors"
                title="Reset Zoom"
              >
                100%
              </button>
              <span className="text-sm text-gray-600 w-12">{Math.round(previewZoom * 100)}%</span>
            </div>

            <div className="w-px h-6 bg-gray-300" />

            {/* Scale Control */}
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Scale:</label>
              <input
                type="range"
                min="0.1"
                max="3"
                step="0.1"
                value={scale}
                onChange={(e) => handleScale(parseFloat(e.target.value))}
                className="w-24"
              />
              <span className="text-sm text-gray-600 w-12">{scale.toFixed(1)}x</span>
            </div>

            <div className="w-px h-6 bg-gray-300" />

            {/* Transparency Control */}
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Transparency:</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={transparency}
                onChange={(e) => handleTransparency(parseFloat(e.target.value))}
                className="w-24"
              />
              <span className="text-sm text-gray-600 w-12">{Math.round(transparency * 100)}%</span>
            </div>

            <div className="w-px h-6 bg-gray-300" />

            {/* Alpha Removal */}
            <button
              onClick={() => setRemoveAlpha(!removeAlpha)}
              className={`px-3 py-1.5 text-sm rounded transition-colors ${
                removeAlpha 
                  ? 'bg-red-600 hover:bg-red-700 text-white' 
                  : 'bg-gray-200 hover:bg-gray-300'
              }`}
            >
              {removeAlpha ? 'Alpha Removed' : 'Remove Alpha'}
            </button>

            <div className="w-px h-6 bg-gray-300" />

            {/* Overlay Controls */}
            <button
              onClick={handleLoadOverlay}
              className="px-3 py-1.5 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
            >
              Load Overlay
            </button>
            
            {overlayImage && (
              <button
                onClick={() => setOverlayImage(null)}
                className="px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
              >
                Remove Overlay
              </button>
            )}

            <div className="w-px h-6 bg-gray-300" />

            <button
              onClick={handleReset}
              className="px-3 py-1.5 text-sm bg-gray-600 hover:bg-gray-700 text-white rounded transition-colors"
            >
              Reset All
            </button>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={handleSaveAs}
              className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors"
            >
              Save As
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || isProcessing}
              className={`px-4 py-1.5 text-sm rounded transition-colors ${
                hasChanges && !isProcessing
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {isProcessing ? 'Processing...' : 'Save'}
            </button>
          </div>
        </div>

        {/* Canvas Area */}
        <div className={`flex-1 overflow-auto bg-gray-100 relative ${
          isMaximized ? 'p-2' : 'p-4'
        }`}>
          <div
            className="relative inline-block mx-auto"
            style={{ zoom: effectivePreviewZoom }}
          >
            <canvas
              ref={canvasRef}
              className="max-w-full h-auto border border-gray-300 shadow-lg"
              style={canvasDisplayStyle}
            />
            {overlayImage && (
              <canvas
                ref={overlayCanvasRef}
                className="absolute top-0 left-0 max-w-full h-auto border border-gray-300 shadow-lg cursor-move"
                style={canvasDisplayStyle}
                onMouseDown={handleOverlayMouseDown}
                onMouseMove={handleOverlayMouseMove}
                onMouseUp={handleOverlayMouseUp}
                onMouseLeave={handleOverlayMouseUp}
              />
            )}
          </div>
        </div>

        {/* Status Bar */}
        <div className="flex items-center justify-between p-2 border-t border-gray-200 text-xs text-gray-500 bg-gray-50">
          <div>
            Original: {imageSize.width} × {imageSize.height}px | 
            Current: {Math.round(imageSize.width * scale)} × {Math.round(imageSize.height * scale)}px
            {overlayImage && ' | Overlay loaded'}
          </div>
          <div>
            {hasChanges ? 'Unsaved' : 'Saved'} | Ctrl+S to save | Esc to close
          </div>
        </div>

        {/* Hidden file input for overlay */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleOverlayFileSelect}
          className="hidden"
        />
      </motion.div>
    </motion.div>
  );
};

export default ImageEditor;
